import React, { JSX, useState, useMemo } from 'react';
import { Box, Table, TableBody, TableContainer, useMediaQuery, useTheme } from '@mui/material';
import { useAuth } from 'hooks/AuthContext';
import * as Types from 'types/types';
import { useAppState } from 'hooks/GlobalContext';
import { DocumentListDialog } from './DocumentListDialog';
import { DocumentListHeader } from './DocumentListHeader';
import { DocumentListRow } from './DocumentListRow';
import { compareDocuments, SortDirection, SortField } from './documentListUtils';

interface DocumentListProps {
  documents: Types.Document[];
  setDocuments: (documents: Types.Document[]) => void;
  setSelectedDocument?: (document: Types.Document | null) => void;
  selectedDocument?: Types.Document | null;
}

const DocumentList = (props: DocumentListProps): JSX.Element => {
  const theme = useTheme();
  const { setSnack } = useAppState();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { apiClient } = useAuth();
  const { documents, setDocuments, setSelectedDocument, selectedDocument } = props;
  const [documentView, setDocumentView] = useState<Types.Document | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [sortField, setSortField] = useState<SortField | null>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Sort documents
  const sortedDocuments = useMemo(() => {
    if (!sortField) return documents;
    return [...documents].sort((a, b) => compareDocuments(a, b, sortField, sortDirection));
  }, [documents, sortField, sortDirection]);

  // Handle sorting
  const handleSort = (field: SortField): void => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const viewDocument = (document: Types.Document, edit = false): void => {
    setDocumentView(document);
    setIsEditing(edit);
  };

  const handleDeleteDocument = async (document: Types.Document): Promise<void> => {
    try {
      await apiClient.deleteCandidateDocument(document);
      setDocuments(documents.filter(doc => doc.id !== document.id));
      setSnack('Document deleted successfully', 'success');

      if (selectedDocument?.id === document.id) {
        setSelectedDocument && setSelectedDocument(null);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRAGToggle = async (
    document: Types.Document,
    includeInRag: boolean
  ): Promise<void> => {
    try {
      document.options = { includeInRag };
      await apiClient.updateCandidateDocument(document);

      setDocuments(
        documents.map(doc => (doc.id === document.id ? { ...doc, options: { includeInRag } } : doc))
      );
      setSnack(`Document ${includeInRag ? 'included in' : 'excluded from'} RAG`, 'success');
    } catch (error) {
      setSnack('Failed to update RAG setting', 'error');
    }
  };

  return (
    <Box
      className="DocumentList"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        p: 0,
        m: 0,
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      <TableContainer>
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
          <DocumentListHeader
            isMobile={isMobile}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <TableBody>
            {sortedDocuments.map(doc => (
              <DocumentListRow
                key={doc.id}
                doc={doc}
                isMobile={isMobile}
                selectedDocument={selectedDocument}
                theme={theme}
                onSelect={() => {
                  setSelectedDocument &&
                    setSelectedDocument(selectedDocument?.id === doc.id ? null : doc);
                }}
                onView={viewDocument}
                onDelete={handleDeleteDocument}
                onRagToggle={handleRAGToggle}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DocumentListDialog
        document={documentView}
        edit={isEditing}
        onSave={() => {
          setDocumentView(null);
          setIsEditing(false);
          setDocuments([...documents]);
        }}
        onCancel={() => {
          setDocumentView(null);
        }}
      />
    </Box>
  );
};

export { DocumentList };
